import { ArrowDownLeftIcon, PlusIcon } from "@heroicons/react/24/solid";
import { useI18N } from "@sundae/react-hooks";
import {
  Balance,
  ButtonGroup,
  CaretDownIcon,
  Collapsible,
  Image,
  InfoCircledIcon,
  Separator,
  Text,
  Tooltip,
  toolkitCx,
} from "@sundae/ui-toolkit";
import cx from "classnames";
import { FC, useCallback, useMemo, useState } from "react";
import { useCurrentTasteTestContribution } from "../../../hooks/taste-test/useCurrentTasteTestContribution";
import { useAdaBalance } from "../../../hooks/useAdaBalance";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import { useProfileAppContext } from "../../../stores";
import { EOrderFlowState, OrderActions, useOrderContext } from "../../../stores/OrderContext";
import FadeInBasic from "../../FadeInView/FadeInBasic";
import TasteTestCurrentPosition from "../TasteTestCurrentPositionDetails";

interface ITasteTestCurrentPositionWidgetProps {
  isSticky?: boolean;
}

const TasteTestCurrentPositionWidget: FC<ITasteTestCurrentPositionWidgetProps> = ({ isSticky }) => {
  const { currentLocale, t } = useI18N("taste-test");
  const [open, setOpen] = useState(false);
  const { adaBalanceFromWallet, isAdaBalanceLessThanMinimum } = useAdaBalance();
  const {
    derived: { hasContribution, latestHistoryRecord },
    handlers: { setActionType, setShowCurrentContribution },
    state: { tasteTestPersonalization },
  } = useTasteTestContext();
  const {
    state: { privacyEnabled },
  } = useProfileAppContext();
  const currentContribution = useCurrentTasteTestContribution();
  const { dispatch } = useOrderContext();
  const adaBalanceZero = adaBalanceFromWallet?.quantity?.amount === 0n;

  const handleHideCurrentPosition = useCallback(() => {
    setShowCurrentContribution(false);
  }, [setShowCurrentContribution]);

  const handleDepositClick = useCallback(() => {
    handleHideCurrentPosition();
    setActionType("update");
  }, [handleHideCurrentPosition, setActionType]);

  const handleWithdrawClick = useCallback(() => {
    handleHideCurrentPosition();
    setActionType("withdraw");
    OrderActions.setOrderFlowState(EOrderFlowState.preview, dispatch);
  }, [dispatch, handleHideCurrentPosition, setActionType]);

  const handleCollapsible = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const primaryButtonLabel = useMemo(() => {
    if (adaBalanceZero || isAdaBalanceLessThanMinimum) {
      return t("warning.title.adaBelowThreshold", { ns: "warnings" });
    }
    return t("detail.widget.currentPositionWidget.button.update");
  }, [
    adaBalanceZero,
    handleHideCurrentPosition,
    isAdaBalanceLessThanMinimum,
    t,
    tasteTestPersonalization?.primaryColor,
  ]);

  if (!hasContribution || !latestHistoryRecord) return null;

  return (
    <div className="flex w-full flex-col gap-4">
      <FadeInBasic
        className={cx(
          "relative flex flex-col gap-4 overflow-hidden rounded-xl",
          toolkitCx.layout.background.default,
          {
            "animation-delay-100 motion-safe:animate-shadow-pulse": isSticky,
          }
        )}
      >
        <Collapsible
          open={open}
          trigger={
            <div
              className={cx(
                "relative w-full pl-28",
                toolkitCx.layout.background.default,
                toolkitCx.layout.spacing.both.md
              )}
            >
              <Image
                src="/static/images/widgets/cards.png"
                alt={t("tasteTest.currentPositionWidget", { ns: "alt-tags" })}
                className="absolute left-2 top-0 h-full"
              />

              <div className="relative z-10 flex w-full flex-col">
                <Balance
                  abbreviate={false}
                  locale={currentLocale}
                  privacy={privacyEnabled}
                  showSymbol
                  size="3xl"
                  value={currentContribution}
                  weight="medium"
                />

                <div className="flex w-full items-center justify-between gap-4">
                  <Text className="flex items-center gap-1" tag="span" size="sm" variant="muted">
                    {t("detail.widget.currentContribution")}{" "}
                    <Tooltip
                      trigger={
                        <InfoCircledIcon
                          className={cx("inline h-4 w-4 cursor-help", toolkitCx.layout.text.muted)}
                        />
                      }
                      tooltipLabel="Current Deposit"
                    />{" "}
                  </Text>

                  <Text
                    tag="div"
                    className="group flex cursor-pointer select-none items-center gap-1"
                    onClick={handleCollapsible}
                    variant="muted"
                  >
                    <Text
                      className={cx("group-hover:opacity-75", toolkitCx.layout.transition.opacity)}
                      tag="span"
                      size="sm"
                      variant="current"
                    >
                      {open ? t("hide", { ns: "common" }) : t("view", { ns: "common" })}{" "}
                      {t("details", {
                        ns: "glossary",
                      })}
                    </Text>
                    <CaretDownIcon
                      className={cx(
                        "h-6 w-6 text-current",
                        "group-hover:opacity-75",
                        toolkitCx.layout.transition.default,
                        toolkitCx.recipes.scaleWhenActive,
                        {
                          "-scale-y-[1]": open,
                        }
                      )}
                    />
                  </Text>
                </div>
              </div>
            </div>
          }
        >
          <Separator orientation="horizontal" className={toolkitCx.layout.background.separator} />
          <div className={toolkitCx.layout.spacing.top.md}>
            <TasteTestCurrentPosition />
          </div>
        </Collapsible>
      </FadeInBasic>

      <FadeInBasic transitionDelay={150}>
        <ButtonGroup
          className="w-full"
          widthRatio={["1/2", "1/2"]}
          size="md"
          primaryButtonConfig={{
            buttonProps: {
              className: "flex-1 group w-full hover:-translate-y-[2px]",
              onClick: handleDepositClick,
              disabled: adaBalanceZero || isAdaBalanceLessThanMinimum,
              style: { backgroundColor: tasteTestPersonalization?.primaryColor || "" },
            },
            icon: PlusIcon,
            iconProps: {
              className: cx("group-hover:scale-110", toolkitCx.layout.transition.transform),
            },
            label: primaryButtonLabel,
          }}
          secondaryButtonConfig={{
            buttonProps: {
              className: "flex-1 group w-full hover:-translate-y-[2px]",
              onClick: handleWithdrawClick,
            },
            icon: ArrowDownLeftIcon,
            iconProps: {
              className: cx("group-hover:scale-110", toolkitCx.layout.transition.transform),
            },
            label: t("detail.widget.currentPositionWidget.button.withdraw"),
          }}
        />
      </FadeInBasic>
    </div>
  );
};

export default TasteTestCurrentPositionWidget;
